/**
 * Edit-state types and a small helper used across the cadence step
 * components. The legacy add/edit JSX components that previously lived
 * here were retired when CampaignDayCardPure took over rendering and
 * editing — only the shared discriminated-union types and `daysToMs`
 * remain.
 */
import type { SiftConfig, StepType } from './CTSContentEditingPure';

export type CadenceStepOption = 'one' | 'two';

/** Edit state for adding a new step */
export type EditAddStepPure = {
  type: 'add';
  afterStepId?: string;
  option: CadenceStepOption;
  /**
   * Disambiguates "add to this day's group" (`'in-group'`, default) from
   * "add a brand-new day before the next card" (`'new-day'`). Both share
   * the same `afterStepId` so the discriminator avoids double-rendering
   * the picker (once in the previous day's card and once in the next day's
   * leading wait band).
   */
  intent?: 'in-group' | 'new-day';
  data?: {
    id: string;
    type: StepType;
    delay: number;
    option?: CadenceStepOption;
    subject?: string | null;
    body2?: string | null;
    postsToLike?: number | null;
    cc?: string | null;
    bcc?: string | null;
    onlyOpenLink?: boolean;
    siftConditions?: SiftConfig | null;
  };
};

/** Edit state for editing delay */
export type EditDelayStepPure = { type: 'delay'; stepId: string };

/** Edit state for editing content */
export type EditContentStepPure = { type: 'edit'; option: CadenceStepOption; stepId: string };

/** Combined edit state */
export type EditStepPure = EditDelayStepPure | EditContentStepPure | EditAddStepPure;

/** Data required to create a new step */
export type CreateStepData = {
  type: StepType;
  delay: number;
  option?: CadenceStepOption;
  afterStepId?: string;
  subject?: string;
  body2?: string;
  postsToLike?: number;
  cc?: string;
  bcc?: string;
  onlyOpenLink?: boolean;
  siftConditions?: SiftConfig;
};

/** Convert days to milliseconds */
export function daysToMs(days: number): number {
  return days * 24 * 60 * 60 * 1000;
}
